export const COMS_HUB_ROLES = ['staff', 'admin', 'dev'];
export const COMS_CHANNEL_LABELS = {
  dev: 'Dev Space',
  staff: 'Staff Space',
  citizen: 'Citizen Space',
};

export const isComsHubUser = (userType) => COMS_HUB_ROLES.includes(userType);

export const getComsBaseUrl = (connectorConfig, defaultComsappBaseUrl) =>
  (connectorConfig?.comsappBaseUrl || defaultComsappBaseUrl || '').replace(/\/$/, '');

export const buildChannelSpaceUrl = (connectorConfig, defaultComsappBaseUrl, channelKey) => {
  const channel = connectorConfig?.channels?.[channelKey];
  if (!channel?.enabled || !channel?.slug || !channel?.spaceId) return '';

  const baseUrl = getComsBaseUrl(connectorConfig, defaultComsappBaseUrl);
  if (!baseUrl) return '';

  return `${baseUrl}/spaces/${channel.spaceId}`;
};

export const getChannelDiagnostics = (connectorConfig, defaultComsappBaseUrl, channelKeys = ['staff', 'dev']) =>
  channelKeys.map((channelKey) => {
    const channel = connectorConfig?.channels?.[channelKey];
    const label = channel?.spaceName || channel?.label || COMS_CHANNEL_LABELS[channelKey] || channelKey;
    const link = buildChannelSpaceUrl(connectorConfig, defaultComsappBaseUrl, channelKey);

    if (!channel?.enabled) {
      return {
        channelKey,
        label,
        link: '',
        severity: 'muted',
        status: 'Disabled',
        detail: 'Channel is not enabled in connector settings.',
      };
    }

    if (!channel?.slug) {
      return {
        channelKey,
        label,
        link: '',
        severity: 'warn',
        status: 'Missing mapping',
        detail: 'Channel is enabled but has no slug configured.',
      };
    }

    if (!channel?.spaceId) {
      return {
        channelKey,
        label,
        link,
        severity: 'muted',
        status: 'Slug configured',
        detail: 'Slug is configured. Canonical space ID will be cached automatically on first live use.',
      };
    }

    return {
      channelKey,
      label,
      link,
      severity: 'ok',
      status: 'Ready',
      detail: `Canonical route ready via spaceId ${channel.spaceId}.`,
    };
  });

const summarizeProvision = (result, config) => {
  if (!result) {
    return { severity: 'muted', title: 'Provision not attempted', detail: '' };
  }

  if (!result.success) {
    const isRateLimited = /too many|rate limit|429/i.test(String(result.message || result.error || ''));
    if (isRateLimited) {
      return {
        severity: 'warn',
        title: 'Provision temporarily throttled',
        detail: 'ComSapp integration is rate-limited right now. Existing channel mappings can still be available below.',
      };
    }

    return {
      severity: 'error',
      title: 'Provision unavailable',
      detail: result.message || result.error || 'ComSapp provisioning could not be completed.',
    };
  }

  const provisionedCount = Array.isArray(result.provisioned) ? result.provisioned.length : 0;
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  const createAccountRequired = skipped.some((item) => item?.reason === 'create_account_required');

  if (createAccountRequired) {
    return {
      severity: 'warn',
      title: 'Account setup required',
      detail: 'At least one ComSapp channel could not provision because the user does not yet have a ComSapp account.',
    };
  }

  if (provisionedCount > 0) {
    return {
      severity: 'ok',
      title: 'Provision checked',
      detail: `Provision verified for ${provisionedCount} channel${provisionedCount === 1 ? '' : 's'}.`,
    };
  }

  const configuredChannels = Object.values(config?.channels || {}).filter((c) => c?.enabled && c?.spaceId);
  if (configuredChannels.length > 0) {
    return {
      severity: 'ok',
      title: 'Channels available',
      detail: 'ComSapp channels are configured and ready from saved connector mappings.',
    };
  }

  return {
    severity: 'muted',
    title: 'No channels provisioned',
    detail: 'No matching ComSapp channels are currently available for this user role.',
  };
};

export const loadComsHubData = async ({ api, currentUser, setConnectorConfig, setDefaultComsappBaseUrl, setMyThreads, setThreadsLoading }) => {
  const configRes = await api.get('/api/connector/config');
  const config = configRes?.data?.config || null;
  const defaultUrl = configRes?.data?.defaultComsappBaseUrl || '';

  setConnectorConfig(config);
  setDefaultComsappBaseUrl(defaultUrl);

  if (!isComsHubUser(currentUser?.userType)) {
    return {
      config,
      defaultUrl,
      provisionStatus: summarizeProvision(null),
    };
  }

  const provisionSessionKey = `comsHubProvisionAttempt:${currentUser?.id || currentUser?._id || currentUser?.email || 'user'}`;
  const lastProvisionAt = Number(sessionStorage.getItem(provisionSessionKey) || '0');
  const canProvisionNow = Date.now() - lastProvisionAt > 30000;

  let provisionResult = {
    success: true,
    skipped: [],
    provisioned: [],
    message: canProvisionNow
      ? ''
      : 'Provision check skipped briefly to avoid integration rate limits.',
  };

  if (canProvisionNow) {
    provisionResult = await api.post('/api/connector/provision-me')
      .then((res) => res?.data || { success: false, message: 'No response from provision endpoint.' })
      .catch((err) => ({
        success: false,
        message: err.response?.data?.message || 'Provision request failed.',
      }));
    sessionStorage.setItem(provisionSessionKey, String(Date.now()));
  }

  const preferredChannel = currentUser?.userType === 'staff' || currentUser?.userType === 'admin'
    ? 'staff'
    : 'dev';

  setThreadsLoading(true);
  let threads = [];
  try {
    const threadsRes = await api.get(`/api/connector/my-threads?channel=${preferredChannel}`);
    threads = threadsRes?.data?.threads || [];
    setMyThreads(threads);
  } finally {
    setThreadsLoading(false);
  }

  return {
    config,
    defaultUrl,
    provisionStatus: summarizeProvision(provisionResult, config),
    threads,
  };
};

// ── Embedded Thread Widget Helpers ──────────────────────────────────────────

/**
 * Fetch user's threads in a channel (for embedded widget preview)
 */
export const fetchUserThreads = async (api, channel = 'dev') => {
  try {
    const response = await api.get(`/api/connector/my-threads?channel=${channel}`);
    const normalizedThreads = (response?.data?.threads || []).map((thread) => {
      const normalizedId = thread?._id || thread?.id || thread?.threadId || null;
      return {
        ...thread,
        _id: normalizedId,
        id: thread?.id || normalizedId,
      };
    });

    return {
      success: true,
      threads: normalizedThreads,
      spaceName: response?.data?.spaceName || channel,
    };
  } catch (error) {
    console.error('[ComsHub] Error fetching user threads:', error);
    return {
      success: false,
      threads: [],
      error: error.response?.data?.message || 'Failed to fetch threads',
    };
  }
};

/**
 * Fetch messages in a thread (for full thread view)
 */
export const fetchThreadMessages = async (api, threadId, channel = 'dev', page = 1, limit = 50) => {
  try {
    if (!threadId) {
      return {
        success: false,
        messages: [],
        error: 'Invalid thread id',
      };
    }

    const response = await api.get(
      `/api/connector/threads/${threadId}/messages?channel=${channel}&page=${page}&limit=${limit}`
    );

    const normalizedMessages = (response?.data?.messages || []).map((message) => {
      const normalizedId = message?._id || message?.id || message?.messageId || null;
      return {
        ...message,
        _id: normalizedId,
        id: message?.id || normalizedId,
      };
    });

    return {
      success: true,
      messages: normalizedMessages,
      pagination: response?.data?.pagination || {},
    };
  } catch (error) {
    console.error('[ComsHub] Error fetching messages:', error);
    return {
      success: false,
      messages: [],
      error: error.response?.data?.message || 'Failed to fetch messages',
    };
  }
};

/**
 * Create a new thread in a channel
 */
export const createNewThread = async (api, { channel = 'dev', subject, type = 'message', tags = [] }) => {
  try {
    if (!subject || subject.trim().length === 0) {
      return { success: false, error: 'Thread subject is required' };
    }

    const response = await api.post('/api/connector/threads', {
      channel,
      subject,
      type,
      tags,
    });

    return {
      success: true,
      thread: response?.data?.thread
        ? {
            ...response.data.thread,
            _id:
              response.data.thread._id ||
              response.data.thread.id ||
              response.data.thread.threadId ||
              null,
          }
        : null,
      deepLink: response?.data?.thread?.deepLink,
    };
  } catch (error) {
    console.error('[ComsHub] Error creating thread:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to create thread',
    };
  }
};

/**
 * Post a message/reply to a thread
 */
export const postThreadMessage = async (api, threadId, { channel = 'dev', body, attachments = [] }) => {
  try {
    if (!threadId) {
      return { success: false, error: 'Invalid thread id' };
    }

    if (!body || body.trim().length === 0) {
      return { success: false, error: 'Message body is required' };
    }

    const response = await api.post(
      `/api/connector/threads/${threadId}/messages`,
      { channel, body, attachments }
    );

    return {
      success: true,
      message: response?.data?.message,
    };
  } catch (error) {
    console.error('[ComsHub] Error posting message:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to post message',
    };
  }
};

/**
 * Delete a thread message
 */
export const deleteThreadMessage = async (api, threadId, messageId, channel = 'dev') => {
  try {
    if (!threadId) {
      return { success: false, error: 'Invalid thread id' };
    }

    if (!messageId) {
      return { success: false, error: 'Invalid message id' };
    }

    const response = await api.delete(
      `/api/connector/threads/${threadId}/messages/${messageId}?channel=${channel}`
    );

    return {
      success: true,
      message: response?.data?.message || 'Message deleted',
    };
  } catch (error) {
    console.error('[ComsHub] Error deleting message:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to delete message',
    };
  }
};

/**
 * Relink ComSapp account (manual reactivation)
 */
export const relinkComsappAccountRequest = async (api) => {
  try {
    const response = await api.post('/api/connector/relink', {});
    return {
      success: true,
      relinked: response?.data?.relinked || [],
      message: response?.data?.message || 'Account relinked successfully',
    };
  } catch (error) {
    console.error('[ComsHub] Error relinking account:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to relink account',
    };
  }
};

/**
 * Get deep-link URL for a channel's mapped space
 */
export const getChannelDeepLink = async (api, channel = 'dev') => {
  try {
    const response = await api.get(`/api/connector/space-deeplink?channel=${channel}`);
    return {
      success: true,
      deepLink: response?.data?.deepLink,
      spaceName: response?.data?.spaceName || channel,
    };
  } catch (error) {
    console.error('[ComsHub] Error getting deep link:', error);
    return {
      success: false,
      deepLink: null,
      error: error.response?.data?.message || 'Failed to get deep link',
    };
  }
};

  /**
   * Get all linked external accounts for the current user
   */
  export const getLinkedAccounts = async (api) => {
    try {
      const response = await api.get('/api/accounts/accounts');
      return {
        success: true,
        accounts: response?.data?.accounts || {},
      };
    } catch (error) {
      console.error('[ComsHub] Error fetching linked accounts:', error);
      return {
        success: false,
        accounts: {},
        error: error.response?.data?.message || 'Failed to fetch linked accounts',
      };
    }
  };

  /**
   * Link an existing ComSapp account by email
   */
  export const linkComsappAccountRequest = async (api, email, rsUserId) => {
    try {
      const response = await api.post('/api/accounts/accounts/link-comsapp', { email, rsUserId });
      return {
        success: true,
        status: response?.status,
        linkedAccount: response?.data?.linkedAccount,
        message: response?.data?.message,
        pendingApproval: response?.data?.pendingApproval,
      };
    } catch (error) {
      console.error('[ComsHub] Error linking ComSapp account:', error);
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
      return {
        success: false,
        status: error.response?.status,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
        error: error.response?.data?.message || 'Failed to link account',
      };
    }
  };

  /**
   * Link an existing ComSapp account by ComSapp user ID (preferred — no email needed)
   */
  export const linkComsappAccountByIdRequest = async (api, comsappUserId, rsUserId) => {
    try {
      const response = await api.post('/api/accounts/accounts/link-comsapp-by-id', { comsappUserId, rsUserId });
      return {
        success: true,
        status: response?.status,
        linkedAccount: response?.data?.linkedAccount,
        message: response?.data?.message,
        pendingApproval: response?.data?.pendingApproval,
      };
    } catch (error) {
      console.error('[ComsHub] Error linking ComSapp account by ID:', error);
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
      return {
        success: false,
        status: error.response?.status,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
        error: error.response?.data?.message || 'Failed to link account',
      };
    }
  };

  /**
   * Unlink a previously linked ComSapp account
   */
  export const unlinkComsappAccountRequest = async (api, accountId) => {
    try {
      const response = await api.delete(`/api/accounts/accounts/comsapp/${accountId}`);
      return {
        success: true,
        message: response?.data?.message,
      };
    } catch (error) {
      console.error('[ComsHub] Error unlinking ComSapp account:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to unlink account',
      };
    }
  };

  /**
   * Create a new ComSapp account with provided credentials and link it
   */
  export const createComsappAccountRequest = async (api, { email, username, password, rsUserId }) => {
    try {
      const response = await api.post('/api/accounts/accounts/create-comsapp', {
        email,
        username,
        password,
        rsUserId,
      });
      return {
        success: true,
        status: response?.status,
        linkedAccount: response?.data?.linkedAccount,
        message: response?.data?.message,
      };
    } catch (error) {
      console.error('[ComsHub] Error creating ComSapp account:', error);
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
      return {
        success: false,
        status: error.response?.status,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
        error: error.response?.data?.message || 'Failed to create account',
      };
    }
  };
